import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAdminStoreAlarms, searchAlrams, getAdminCustAlarms, searchCustAlarms } from "../../utils/alarmService";
import { formatDateTime24 } from "../../utils/commonService";
import "./css/adminalarm.css";
import Pagination from "../common/Pagination";

const AdminAlarm = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("store");
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  // 오늘 날짜 구하기
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(getTodayDate());
  const [alarmType, setAlarmType] = useState("all");
  const [searchType, setSearchType] = useState("all");
  const [searchKeyword, setSearchKeyword] = useState("");

  // content에서 ISO 형식의 datetime을 한국식으로 포맷팅
  const formatContentDateTime = (content) => {
    if (!content) return content;
    
    // ISO 형식의 datetime 패턴 (예: 2026-01-23T10:30:00)
    const isoDateTimePattern = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/g;
    
    return content.replace(isoDateTimePattern, (match) => {
      const date = new Date(match);
      return date.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    });
  };

  // const [isSearchMode, setIsSearchMode] = useState(false);

  const fetchAlarms = useCallback(async (page = 0) => {
  setLoading(true);
  setError(null);

  try {
    const fetchFn = activeTab === "cust" ? getAdminCustAlarms : getAdminStoreAlarms;
    const data = await fetchFn(page, pageSize);

    setAlarms(data.content || []);
    setCurrentPage(data.number || 0);
    setTotalPages(data.totalPages || 0);
    setTotalElements(data.totalElements || 0);
  } catch (err) {
    setError(err.message || "알림 목록 조회에 실패했습니다.");
    console.error("알림 목록 조회 실패:", err);
    setAlarms([]);
  } finally {
    setLoading(false);
  }
}, [activeTab, pageSize]);


useEffect(() => {
  fetchAlarms(0);
}, [fetchAlarms]);


  const handleSearchInternal = async (page = 0) => {
    const searchParams = {
      startDate: startDate,
      endDate: endDate,
      alarmType: alarmType,
      searchTerm: searchKeyword,
      searchType: searchType,
    };

    setLoading(true);
    setError(null);

    try {
      const searchFn = activeTab === "cust" ? searchCustAlarms : searchAlrams;
      const result = await searchFn(searchParams, page, pageSize);
      if (result.success) {
        setAlarms(result.data.content);
        setCurrentPage(result.data.number);
        setTotalPages(result.data.totalPages);
        setTotalElements(result.data.totalElements);
      } else {
        setError(result.error);
        alert(result.error);
      }
    } catch (err) {
      setError(err.message);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchAlarms(page);
  };

  // 검색 버튼
  const handleSearch = () => {
    setCurrentPage(0);
    handleSearchInternal(0);
  };
  // 페이지 이동 - AlarmType에 따라 적절한 페이지로 이동
  const handleNavigate = (alarm) => {
    if (!alarm.alarmLink) return;
    // 알람 타입에 따라 관리자 페이지로 이동
    switch (alarm.alarmType) {
      case "주문":
        // 주문 관리 페이지로 이동
        navigate("/admin/order", {
          state: {
            openModal: true,
            orderCode: alarm.alarmLink,
          },
        });
        break;
      case "리뷰":
        // 리뷰 관리 페이지로 이동
        navigate("/admin/review", {
          state: {
            openModal: true,
            reviewIdx: alarm.alarmLink,
          },
        });
        break;
      case "상품신고":
        // 상품 신고 관리 페이지로 이동
        navigate("/admin/report", {
          state: {
            openModal: true,
            productCode: alarm.alarmLink,
            reportType: "PRODUCT",
          },
        });
        break;
      case "리뷰신고":
        // 리뷰 신고 관리 페이지로 이동
        navigate("/admin/report", {
          state: {
            openModal: true,
            reviewIdx: alarm.alarmLink,
            reportType: "REVIEW",
          },
        });
        break;
      case "가맹신고":
        // 가맹점 신고 관리 페이지로 이동
        navigate("/admin/report", {
          state: {
            openModal: true,
            storeCode: alarm.alarmLink,
            reportType: "STORE",
          },
        });
        break;
      case "네고":
        // 네고 관리 페이지로 이동
        navigate("/admin/negotiation", {
          state: {
            openModal: true,
            negoId: alarm.alarmLink,
          },
        });
        break;
      case "문의":
        // 문의 관리 페이지로 이동
        navigate("/admin/question", {
          state: {
            openModal: true,
            questionIdx: alarm.alarmLink,
          },
        });
        break;
        case "상품 제재":
        // 상품 제재 관리 페이지로 이동
        navigate("/admin/product", {
          state: {
            openModal: true,
            productCode: alarm.alarmLink,
          },
        });
        break;
      default:
        break;
    }
  };

  return (
    <div className="adminalarm-container">
      <div className="adminalarm-wrap">
        {/* 탭 네비게이션 */}
        <div className="adminalarm-tab-navigation">
          <button
            className={`adminalarm-tab-btn ${
              activeTab === "store" ? "adminalarm-tab-active" : ""
            }`}
            onClick={() => { setActiveTab("store"); setAlarmType(""); setSearchType("all"); setSearchKeyword(""); }}
          >
            가맹점 알림
          </button>
          <button
            className={`adminalarm-tab-btn ${
              activeTab === "cust" ? "adminalarm-tab-active" : ""
            }`}
            onClick={() => { setActiveTab("cust"); setAlarmType(""); setSearchType("all"); setSearchKeyword(""); }}
          >
            일반사용자 알림
          </button>
        </div>
        <div className="adminalarm-filter-wrap">
          <div className="adminalarm-filter-row">
            <div className="adminalarm-filter-group">
              <label htmlFor="adminalarm-start-date">조회기간</label>
              <input
                type="date"
                id="adminalarm-start-date"
                className="adminalarm-filter-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>~</span>
              <input
                type="date"
                className="adminalarm-filter-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="adminalarm-filter-group">
              <label htmlFor="alarm-type">알림타입</label>
              <select
                name="alarm-type"
                id="alarm-type"
                className="adminalarm-select"
                value={alarmType}
                onChange={(e) => setAlarmType(e.target.value)}
              >
                {activeTab === "store" ? (
                  <>
                    <option value="">전체</option>
                    <option value="ORDER">주문</option>
                    <option value="NEGO">네고</option>
                    <option value="NEGO_REQUEST">네고 요청</option>
                    <option value="REVIEW">리뷰</option>
                    <option value="REVIEW_HIDE">리뷰 숨김</option>
                    <option value="REPORT_PRODUCT">상품신고</option>
                    <option value="REPORT_REVIEW">리뷰신고</option>
                    <option value="REPORT_STORE">가맹신고</option>
                    <option value="QUESTION">문의</option>
                    <option value="PRODUCT_RESTRICT">상품 제재</option>
                    <option value="SALE">할인</option>
                    <option value="MEMBERSHIP_PAYMENT">구독 결제</option>
                  </>
                ) : (
                  <>
                    <option value="">전체</option>
                    <option value="PAYMENT">결제</option>
                    <option value="PRODUCT_ORDER_CANCEL">주문취소</option>
                    <option value="PRODUCT_ORDER_REFUND">주문환불</option>
                    <option value="PRODUCT_NEGO_REQUEST">네고 요청</option>
                    <option value="PRODUCT_NEGO_APPROVE">네고 요청 승인</option>
                    <option value="PRODUCT_NEGO_REJECT">네고 요청 거절</option>
                    <option value="PRODUCT_NEGO_CANCEL">네고 요청 취소</option>
                    <option value="REVIEW_REQUEST">리뷰요청</option>
                    <option value="REVIEW_ANSWER">리뷰답변</option>
                    <option value="STORE_SALE">관심매장 할인</option>
                    <option value="STORE_NEGO">관심매장 네고</option>
                    <option value="CS_REPORT_ANSWER">신고 답변</option>
                    <option value="CS_QUESTION_ANSWER">문의 답변</option>
                  </>
                )}
              </select>
            </div>
          </div>
          <div className="adminalarm-filter-row adminalarm-search-row">
            <div className="adminalarm-search-group">
              <select
                name="alarm-option"
                id="alarm-option"
                className="adminalarm-select-type"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
              >
                <option value="all">전체</option>
                {activeTab === "store" ? (
                  <option value="storeCode">가맹코드</option>
                ) : (
                  <option value="custCode">회원코드</option>
                )}
                <option value="content">내용</option>
              </select>
              <input
                type="text"
                className="adminalarm-input"
                placeholder="검색어를 입력하세요"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <button className="adminalarm-search-btn" onClick={handleSearch}>
                검색
              </button>
              <button
                className="adminalarm-reset-btn"
                onClick={() => {
                  setStartDate("");
                  setEndDate(getTodayDate());
                  setAlarmType("");
                  setSearchType("all");
                  setSearchKeyword("");
                  setCurrentPage(0);
                  fetchAlarms(0);
                }}
              >
                초기화
              </button>
            </div>
          </div>
        </div>
        <div className="adminalarm-table-wrap">
          <div className="adminalarm-table-header">
            <div className="adminalarm-table-info">
              총 <b>{totalElements}</b>건
            </div>
          </div>
          {loading ? (
            <div className="adminalarm-loading">로딩 중...</div>
          ) : error ? (
            <div className="adminalarm-error">{error}</div>
          ) : (
            <>
              <table className="adminalarm-table">
                <thead>
                  <tr>
                    {activeTab === "store" ? (
                      <>
                        <th>가맹코드</th>
                        <th>가맹점</th>
                        <th>회원ID</th>
                      </>
                    ) : (
                      <>
                        <th>회원코드</th>
                        <th>회원ID</th>
                      </>
                    )}
                    <th>알림타입</th>
                    <th>내용</th>
                    <th>전송시간</th>
                    <th>읽은시간</th>
                  </tr>
                </thead>
                <tbody>
                  {alarms.length === 0 ? (
                    <tr>
                      <td
                        colSpan={activeTab === "store" ? 8 : 7}
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#6b7280",
                        }}
                      >
                        조회된 알림이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    alarms.map((alarm, index) => (
                      <tr key={alarm.alarmIdx}>
                        {activeTab === "store" ? (
                          <>
                            <td>{alarm.storeCode || "-"}</td>
                            <td>{alarm.storeNm || "-"}</td>
                            <td>{alarm.memberId || "-"}</td>
                          </>
                        ) : (
                          <>
                            <td>{alarm.custCode || "-"}</td>
                            <td>{alarm.custId || "-"}</td>
                          </>
                        )}
                        <td>{activeTab === "store" ? alarm.storeAlarmType : alarm.custAlarmType}</td>
                        <td className="adminalarm-content-cell">
                          {formatContentDateTime(alarm.content)}
                        </td>
                        <td>{formatDateTime24(alarm.sendDateTime)}</td>
                        <td>{formatDateTime24(alarm.readDateTime)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default AdminAlarm;
